/**
 * Canvas asset download filenames — single source of truth.
 *
 * Every download surface in the console (per-clip header, Variants tab,
 * per-designer thumbnail, storyboard grid, sound expert, bulk Clip Download
 * modal) routes through `buildClipDownloadFilename` or
 * `buildAssetDownloadFilename` so a downloaded asset has the same name no
 * matter which button the user clicks.
 *
 * Format:
 *   clip videos:  Episode_<EE>_clip_<CC>[_<collision>]-v<VV>[_remix_<RR>].<ext>
 *   images:       Episode_<EE>_<typeslug>_<NN>[_v<VV>].<ext>
 *   audio:        Episode_<EE>_<typeslug>_<NN>[_v<VV>][_remix_<RR>].<ext>
 *
 * Pure module — no React, no DOM imports.
 */

import type { Edge } from 'reactflow'
import { createNadDebug } from './debugLog'
import {
  getTypedNodeData,
  isTypedReactFlowNode,
  type AudioAssetRefDict,
  type BasicAudio,
  type BasicImage,
  type BasicVideo,
  type GeneratedAssetMetadata,
  type GeneratedAudio,
  type GeneratedAudioWithHistory,
  type GeneratedImage,
  type GeneratedImageWithHistory,
  type GeneratedVideo,
  type GeneratedVideoWithHistory,
  type ImageAssetRefDict,
  type SoundAudioHistoryEntryDict,
  type SoundGeneratedAudioDict,
  type SoundGenerationMetadataDict,
  type UntypedReactFlowNode,
  type VideoAssetRefDict,
  type _SoundAudioResultDict,
} from '../generated/nodeDataTypes'
import {
  getVariantOrdinals,
  type RemixMetadata,
  type VideoHistoryEntry,
  type VideoHistoryResult,
} from '../components/canvas-v2/nodes/shared/variantOrdinals'
import { getDesignerMainImage } from '../components/canvas-v2/nodes/shared/designerUtils'

const filenameLog = createNadDebug('dw:asset-download-filename')

// ─── Public types ───────────────────────────────────────────────────────────

export type DownloadMediaType = 'image' | 'video' | 'audio'

export type DownloadFilenameTrigger =
  | 'bulk-download'
  | 'node-asset'
  | 'node-header'
  | 'unspecified'
  | 'variants-panel'

export type EpisodeSource = 'video-scene-id' | 'writer-name' | 'writer-position'

export type FallbackReason = 'no-binding-writer' | 'no-clip-number' | 'collision'

export type CanvasNode = UntypedReactFlowNode
export type CanvasEdge = Edge
type AssetHistoryPortValue =
  | Pick<AudioAssetRefDict, 'history' | 'picked_index'>
  | Pick<GeneratedAudioWithHistory, 'history' | 'picked_index'>
  | Pick<GeneratedImageWithHistory, 'history' | 'picked_index'>
  | Pick<GeneratedVideoWithHistory, 'history' | 'picked_index'>
  | Pick<ImageAssetRefDict, 'history' | 'picked_index'>
  | Pick<SoundGeneratedAudioDict, 'history' | 'picked_index'>
  | Pick<VideoAssetRefDict, 'history' | 'picked_index'>

export interface NodeAssetInfo {
  mediaType: DownloadMediaType
  assetPath: string
  variantHistory?: VideoHistoryEntry[]
  pickedIndex?: number
}

export interface ClipFilenameInputs {
  canvasNodes: CanvasNode[]
  canvasEdges: CanvasEdge[]
  nodeId: string
  variantHistory: VideoHistoryEntry[]
  pickedIndex: number
  assetPath: string
  trigger?: DownloadFilenameTrigger
}

export interface AssetFilenameInputs {
  canvasNodes: CanvasNode[]
  canvasEdges: CanvasEdge[]
  nodeId: string
  assetPath: string
  mediaType: DownloadMediaType
  variantHistory?: VideoHistoryEntry[]
  pickedIndex?: number
  trigger?: DownloadFilenameTrigger
}

export interface FilenameComponents {
  episodeOrdinal: number | null
  episodeSource: EpisodeSource | null
  typeslug: string
  sequenceOrdinal: number
  collisionSuffix: string | null
  variantOrdinal: number | null
  remixOrdinal: number | null
  sourceOriginalOrdinal: number | null
  widths: { episode: number; sequence: number; variant: number }
  ext: string
  fallbackReason: FallbackReason | null
}

export interface FilenameResult {
  filename: string
  components: FilenameComponents
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const NODE_TYPESLUG: Record<string, string> = {
  videoGenerator: 'clip',
  imageGenerator: 'image',
  characterDesigner: 'character',
  locationDesigner: 'location',
  objectDesigner: 'object',
  storyboardDesigner: 'storyboard',
  storyboardNoEnhanceDesigner: 'storyboard',
  soundExpert: 'audio',
  videoEditor: 'edit',
  image: 'upload',
  video: 'upload',
  audio: 'upload',
}

export function typeslugFor(nodeType: string | undefined): string {
  if (!nodeType) return 'asset'
  return NODE_TYPESLUG[nodeType] ?? nodeType.replace(/Designer$/, '').toLowerCase()
}

const PANEL_INCOMING_HANDLES = new Set(['in_prompt', 'in_image', 'in_panel_refs'])

const COLLISION_LETTERS = 'bcdefghijklmnopqrstuvwxyz'

const EXT_FALLBACK: Record<DownloadMediaType, string> = {
  image: 'png',
  video: 'mp4',
  audio: 'mp3',
}

// ─── Walker helpers ─────────────────────────────────────────────────────────

function groupBy<T, K>(items: T[], key: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>()
  for (const item of items) {
    const k = key(item)
    const list = map.get(k)
    if (list) list.push(item)
    else map.set(k, [item])
  }
  return map
}

/**
 * Pre-pass: identify panel image-generator nodes — those with an incoming
 * edge from a storyboardDesigner via `in_prompt` / `in_image` /
 * `in_panel_refs`. Only these are admitted by the walker as upstream
 * imageGenerator sources.
 */
function findPanelImageGenerators(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
): Set<string> {
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const result = new Set<string>()
  for (const edge of edges) {
    const target = nodeById.get(edge.target)
    if (target?.type !== 'imageGenerator') continue
    if (!PANEL_INCOMING_HANDLES.has(edge.targetHandle ?? '')) continue
    const source = nodeById.get(edge.source)
    if (source?.type !== 'storyboardDesigner' && source?.type !== 'storyboardNoEnhanceDesigner') continue
    result.add(edge.target)
  }
  return result
}

function findBindingScriptWriterAndStoryboard(
  startNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  panelImageGenerators: Set<string>,
): { writer: CanvasNode | null; storyboard: CanvasNode | null } {
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const incomingByTarget = groupBy(edges, (e) => e.target)
  const queue: Array<{ id: string; depth: number }> = [{ id: startNodeId, depth: 0 }]
  const visited = new Set<string>([startNodeId])
  let firstStoryboard: CanvasNode | null = null

  while (queue.length) {
    const { id, depth } = queue.shift()!
    if (depth > 32) continue
    const node = nodeById.get(id)
    if (
      !firstStoryboard &&
      (node?.type === 'storyboardDesigner' || node?.type === 'storyboardNoEnhanceDesigner')
    ) {
      firstStoryboard = node
    }
    if (node?.type === 'scriptWriter') {
      return { writer: node, storyboard: firstStoryboard }
    }
    for (const edge of incomingByTarget.get(id) ?? []) {
      const source = nodeById.get(edge.source)
      if (!source) continue
      const t = source.type
      const admit =
        t === 'scriptWriter' ||
        t === 'storyboardDesigner' ||
        t === 'storyboardNoEnhanceDesigner' ||
        (t === 'imageGenerator' && panelImageGenerators.has(source.id))
      if (!admit) continue
      if (visited.has(edge.source)) continue
      visited.add(edge.source)
      queue.push({ id: edge.source, depth: depth + 1 })
    }
  }
  return { writer: null, storyboard: firstStoryboard }
}

// ─── Episode ordinal extraction ─────────────────────────────────────────────

function parseTrailingOrdinal(s: string | undefined | null): number | null {
  if (typeof s !== 'string') return null
  const normalized = s.toLowerCase().replace(/[^a-z0-9]+/g, ' ')
  const m = normalized.match(/(\d+)\s*$/)
  return m ? parseInt(m[1], 10) : null
}

/** Parse canonical video scene IDs such as `ep5_trial_of_fire`. */
function parseEpisodeOrdinal(s: string | undefined | null): number | null {
  if (typeof s !== 'string') return null
  const canonical = s.trim().match(/^(?:ep|episode)[\s_-]*(\d+)(?:$|[\s_-])/i)
  if (canonical) return parseInt(canonical[1], 10)
  return parseTrailingOrdinal(s)
}

function extractEpisodeOrdinal(
  assetNode: CanvasNode,
  bindingWriter: CanvasNode | null,
  allScriptWriters: CanvasNode[],
): { ordinal: number | null; source: EpisodeSource | null } {
  // Only videoGenerator carries an episode-encoded scene id (the Director
  // sets `in_scene_id="episode_<N>"` per director_tools.py:2288-2294).
  // storyboardDesigner.in_scene_id is the scene number within an episode
  // (e.g. "scene_07") — not the episode — so it is not parsed here.
  const videoData = getTypedNodeData(assetNode, 'videoGenerator')
  if (videoData) {
    const fromVideoScene = parseEpisodeOrdinal(videoData.in_scene_id)
    if (fromVideoScene != null) return { ordinal: fromVideoScene, source: 'video-scene-id' }
  }
  if (bindingWriter && isTypedReactFlowNode(bindingWriter, 'scriptWriter')) {
    const fromWriterName = parseTrailingOrdinal(bindingWriter.data.in_name)
    if (fromWriterName != null) return { ordinal: fromWriterName, source: 'writer-name' }
    const sorted = [...allScriptWriters].sort((a, b) => a.id.localeCompare(b.id))
    const idx = sorted.findIndex((w) => w.id === bindingWriter.id)
    if (idx >= 0) return { ordinal: idx + 1, source: 'writer-position' }
  }
  return { ordinal: null, source: null }
}

// ─── Padding widths ─────────────────────────────────────────────────────────

interface PaddingWidths {
  episode: number
  clip: number
  variant: number
  sequencePerType: Record<string, number>
}

function projectPaddingWidths(nodes: CanvasNode[]): PaddingWidths {
  let episodeCount = 0
  let maxClipNumber = 0
  let videoCount = 0
  let maxVariantHistory = 0
  const perTypeCount: Record<string, number> = {}

  const trackHistory = (length: number | undefined) => {
    if (length != null && length > maxVariantHistory) maxVariantHistory = length
  }

  for (const n of nodes) {
    const t = n.type ?? ''
    if (t === 'scriptWriter') episodeCount++

    const videoData = getTypedNodeData(n, 'videoGenerator')
  if (videoData) {
    videoCount++
    const c = videoData.in_clip_number ?? 0
    if (c > maxClipNumber) maxClipNumber = c
      trackHistory(videoData.out_video?.history?.length)
    } else {
      perTypeCount[t] = (perTypeCount[t] ?? 0) + 1
    }

    const imgGen = getTypedNodeData(n, 'imageGenerator')
    if (imgGen) trackHistory(imgGen.out_image?.history?.length)
    const character = getTypedNodeData(n, 'characterDesigner')
    if (character) trackHistory(getDesignerMainImage(character.out_image)?.history?.length)
    const location = getTypedNodeData(n, 'locationDesigner')
    if (location) trackHistory(getDesignerMainImage(location.out_image)?.history?.length)
    const object = getTypedNodeData(n, 'objectDesigner')
    if (object) trackHistory(getDesignerMainImage(object.out_image)?.history?.length)
    const sound = getTypedNodeData(n, 'soundExpert')
    if (sound) trackHistory(sound.out_audio?.history?.length)
  }

  const w = (n: number) => Math.max(2, String(Math.max(n, 1)).length)
  return {
    episode: w(episodeCount),
    clip: w(maxClipNumber || videoCount),
    variant: w(maxVariantHistory),
    sequencePerType: Object.fromEntries(Object.entries(perTypeCount).map(([k, v]) => [k, w(v)])),
  }
}

// ─── Per-node-type extractor ────────────────────────────────────────────────

function readPickedFromHistory(
  history: VideoHistoryEntry[] | undefined,
  pickedIndex: number,
): string | undefined {
  if (!history || history.length === 0) return undefined
  const safeIdx = pickedIndex >= 0 && pickedIndex < history.length ? pickedIndex : 0
  return history[safeIdx]?.result?.local_path
}

type AssetHistoryEntry =
  | GeneratedAudio
  | GeneratedImage
  | GeneratedVideo
  | SoundAudioHistoryEntryDict

type AssetHistoryResult =
  | BasicAudio
  | BasicImage
  | BasicVideo
  | _SoundAudioResultDict

function historyResultToVariantResult(
  result: AssetHistoryResult | undefined,
): VideoHistoryResult | undefined {
  if (!result) return undefined
  return {
    id: 'id' in result ? result.id : undefined,
    local_path: result.local_path,
    metadata: result.metadata,
  }
}

function historyMetadataToVariantMetadata(
  metadata: GeneratedAssetMetadata | SoundGenerationMetadataDict | undefined,
): RemixMetadata | undefined {
  return metadata
}

function historyEntryToVariantEntry(entry: AssetHistoryEntry): VideoHistoryEntry {
  return {
    user_prompt: entry.user_prompt,
    result: historyResultToVariantResult(entry.result),
    metadata: historyMetadataToVariantMetadata(entry.metadata),
  }
}

function assetHistoryToVariantHistory(
  asset: AssetHistoryPortValue | null | undefined,
): VideoHistoryEntry[] | undefined {
  return asset?.history?.map(historyEntryToVariantEntry)
}

function readPickedAssetPath(asset: AssetHistoryPortValue | null | undefined): string | undefined {
  return readPickedFromHistory(assetHistoryToVariantHistory(asset), asset?.picked_index ?? 0)
}

/**
 * Resolve a designer node's downloadable image.
 *
 * Designer selections use the historical asset wrapper's `picked_index` as
 * the single source of truth.
 */
function designerAssetInfo(
  entries: AssetHistoryEntry[] | undefined,
  blobPickedIndex: number | undefined,
): NodeAssetInfo | null {
  const history = entries?.map(historyEntryToVariantEntry)
  const resolvedIndex = resolveDesignerVariantIndex(history, blobPickedIndex)
  const fromHistory = readPickedFromHistory(history, resolvedIndex)
  const path = fromHistory
  if (!path) return null
  return { mediaType: 'image', assetPath: path, variantHistory: history, pickedIndex: resolvedIndex }
}

function resolveDesignerVariantIndex(
  history: VideoHistoryEntry[] | undefined,
  blobPickedIndex: number | undefined,
): number {
  if (!history || history.length === 0) return 0
  if (typeof blobPickedIndex === 'number' && blobPickedIndex >= 0 && blobPickedIndex < history.length) {
    return blobPickedIndex
  }
  return 0
}

export function extractNodeAssetInfo(node: CanvasNode): NodeAssetInfo | null {
  const video = getTypedNodeData(node, 'videoGenerator')
  if (video) {
    const history = assetHistoryToVariantHistory(video.out_video) ?? []
    const pickedIndex = video.out_video?.picked_index ?? 0
    const fromHistory = readPickedFromHistory(history, pickedIndex)
    const path = fromHistory
    if (!path) return null
    return { mediaType: 'video', assetPath: path, variantHistory: history, pickedIndex }
  }
  const image = getTypedNodeData(node, 'imageGenerator')
  if (image) {
    const history = assetHistoryToVariantHistory(image.out_image) ?? []
    const pickedIndex = image.out_image?.picked_index ?? 0
    const fromHistory = readPickedFromHistory(history, pickedIndex)
    const path = fromHistory
    if (!path) return null
    return { mediaType: 'image', assetPath: path, variantHistory: history, pickedIndex }
  }
  const character = getTypedNodeData(node, 'characterDesigner')
  if (character) {
    const main = getDesignerMainImage(character.out_image)
    const history = main?.history
    return designerAssetInfo(history, main?.picked_index)
  }
  const location = getTypedNodeData(node, 'locationDesigner')
  if (location) {
    const main = getDesignerMainImage(location.out_image)
    const history = main?.history
    return designerAssetInfo(history, main?.picked_index)
  }
  const object = getTypedNodeData(node, 'objectDesigner')
  if (object) {
    const main = getDesignerMainImage(object.out_image)
    const history = main?.history
    return designerAssetInfo(history, main?.picked_index)
  }
  const storyboard = getTypedNodeData(node, 'storyboardDesigner')
  if (storyboard) {
    const history = assetHistoryToVariantHistory(storyboard.out_image) ?? []
    const pickedIndex = storyboard.out_image?.picked_index ?? 0
    const fromHistory = readPickedFromHistory(history, pickedIndex)
    const path = fromHistory
    if (!path) return null
    return { mediaType: 'image', assetPath: path, variantHistory: history, pickedIndex }
  }
  const storyboardNoEnhance = getTypedNodeData(node, 'storyboardNoEnhanceDesigner')
  if (storyboardNoEnhance) {
    const path = readPickedAssetPath(storyboardNoEnhance.out_image)
    if (!path) return null
    return { mediaType: 'image', assetPath: path }
  }
  const sound = getTypedNodeData(node, 'soundExpert')
  if (sound) {
    const history = assetHistoryToVariantHistory(sound.out_audio) ?? []
    const pickedIndex = sound.out_audio?.picked_index ?? 0
    const fromHistory = readPickedFromHistory(history, pickedIndex)
    const path = fromHistory
    if (!path) return null
    return { mediaType: 'audio', assetPath: path, variantHistory: history, pickedIndex }
  }
  const editor = getTypedNodeData(node, 'videoEditor')
  if (editor) {
    const path = readPickedAssetPath(editor.out_video)
    if (!path) return null
    return { mediaType: 'video', assetPath: path }
  }
  const imageDisplay = getTypedNodeData(node, 'image')
  if (imageDisplay) {
    const path = readPickedAssetPath(imageDisplay.out_image)
    if (!path) return null
    return { mediaType: 'image', assetPath: path }
  }
  const videoDisplay = getTypedNodeData(node, 'video')
  if (videoDisplay) {
    const path = readPickedAssetPath(videoDisplay.out_video)
    if (!path) return null
    return { mediaType: 'video', assetPath: path }
  }
  const audioDisplay = getTypedNodeData(node, 'audio')
  if (audioDisplay) {
    const path = readPickedAssetPath(audioDisplay.out_audio)
    if (!path) return null
    return { mediaType: 'audio', assetPath: path }
  }
  return null
}

// ─── Sequence + collision helpers ───────────────────────────────────────────

function parseExtension(assetPath: string, mediaType: DownloadMediaType): string {
  const dot = assetPath.lastIndexOf('.')
  if (dot < 0) return EXT_FALLBACK[mediaType]
  const raw = assetPath
    .slice(dot + 1)
    .split(/[?#]/, 1)[0]
    .toLowerCase()
  if (!raw || raw.length > 5) return EXT_FALLBACK[mediaType]
  return raw
}

interface VideoEpisodeMap {
  episodeOrdinalByVideo: Map<string, number | null>
  videoIdsByEpisode: Map<number | null, string[]>
}

function collectVideoGeneratorsByEpisode(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
): VideoEpisodeMap {
  const panelSet = findPanelImageGenerators(nodes, edges)
  const allScriptWriters = nodes.filter((n) => n.type === 'scriptWriter')
  const episodeOrdinalByVideo = new Map<string, number | null>()
  const videoIdsByEpisode = new Map<number | null, string[]>()
  for (const n of nodes) {
    if (!isTypedReactFlowNode(n, 'videoGenerator')) continue
    const { writer } = findBindingScriptWriterAndStoryboard(n.id, nodes, edges, panelSet)
    const { ordinal } = extractEpisodeOrdinal(n, writer, allScriptWriters)
    episodeOrdinalByVideo.set(n.id, ordinal)
    const bucket = videoIdsByEpisode.get(ordinal) ?? []
    bucket.push(n.id)
    videoIdsByEpisode.set(ordinal, bucket)
  }
  for (const [k, v] of videoIdsByEpisode) {
    v.sort((a, b) => a.localeCompare(b))
    videoIdsByEpisode.set(k, v)
  }
  return { episodeOrdinalByVideo, videoIdsByEpisode }
}

function detectClipSequence(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  videoNode: CanvasNode,
  episodeOrdinal: number | null,
): { sequenceOrdinal: number; collisionSuffix: string | null; fallbackReason: FallbackReason | null } {
  const data = getTypedNodeData(videoNode, 'videoGenerator')
  const clipNumber = data?.in_clip_number ?? 0
  const videoMap = collectVideoGeneratorsByEpisode(nodes, edges)
  if (clipNumber > 0) {
    // Collision: more than one videoGenerator with the same (episode, clip_number).
    const colliders: string[] = []
    for (const n of nodes) {
      const d = getTypedNodeData(n, 'videoGenerator')
      if (!d) continue
      if ((d.in_clip_number ?? 0) !== clipNumber) continue
      if ((videoMap.episodeOrdinalByVideo.get(n.id) ?? null) !== episodeOrdinal) continue
      colliders.push(n.id)
    }
    colliders.sort((a, b) => a.localeCompare(b))
    const idx = colliders.indexOf(videoNode.id)
    if (colliders.length > 1 && idx > 0) {
      const letter = COLLISION_LETTERS[idx - 1] ?? COLLISION_LETTERS[COLLISION_LETTERS.length - 1]
      return { sequenceOrdinal: clipNumber, collisionSuffix: letter, fallbackReason: 'collision' }
    }
    return { sequenceOrdinal: clipNumber, collisionSuffix: null, fallbackReason: null }
  }
  // No clip number — positional within episode.
  const bucket = videoMap.videoIdsByEpisode.get(episodeOrdinal) ?? []
  const idx = bucket.indexOf(videoNode.id)
  return {
    sequenceOrdinal: idx >= 0 ? idx + 1 : 1,
    collisionSuffix: null,
    fallbackReason: 'no-clip-number',
  }
}

function detectAssetSequence(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  assetNode: CanvasNode,
  episodeOrdinal: number | null,
): number {
  const nodeType = assetNode.type ?? ''
  // Special case: storyboard sequence parsed from in_scene_id trailing integer.
  const storyboard = getTypedNodeData(assetNode, 'storyboardDesigner')
  if (storyboard) {
    const parsed = parseTrailingOrdinal(storyboard.in_scene_id)
    if (parsed != null) return parsed
  }
  const sbNoEnhance = getTypedNodeData(assetNode, 'storyboardNoEnhanceDesigner')
  if (sbNoEnhance) {
    const parsed = parseTrailingOrdinal(sbNoEnhance.in_scene_id)
    if (parsed != null) return parsed
  }
  // Positional within binding episode, scoped to same node.type.
  const panelSet = findPanelImageGenerators(nodes, edges)
  const allScriptWriters = nodes.filter((n) => n.type === 'scriptWriter')
  const sameTypeInEpisode: string[] = []
  for (const n of nodes) {
    if (n.type !== nodeType) continue
    const { writer: w } = findBindingScriptWriterAndStoryboard(n.id, nodes, edges, panelSet)
    const { ordinal } = extractEpisodeOrdinal(n, w, allScriptWriters)
    if (ordinal === episodeOrdinal) sameTypeInEpisode.push(n.id)
  }
  sameTypeInEpisode.sort((a, b) => a.localeCompare(b))
  const idx = sameTypeInEpisode.indexOf(assetNode.id)
  return idx >= 0 ? idx + 1 : 1
}

// ─── Format functions ───────────────────────────────────────────────────────

function pad(n: number, w: number): string {
  return String(n).padStart(w, '0')
}

function formatClipFilename(c: FilenameComponents): string {
  const eps = c.episodeOrdinal != null ? `Episode_${pad(c.episodeOrdinal, c.widths.episode)}_` : ''
  const clip = `clip_${pad(c.sequenceOrdinal, c.widths.sequence)}`
  const coll = c.collisionSuffix ? `_${c.collisionSuffix}` : ''
  const vOrdinal =
    c.remixOrdinal != null && c.sourceOriginalOrdinal != null
      ? c.sourceOriginalOrdinal
      : c.variantOrdinal
  const v = vOrdinal != null ? `-v${pad(vOrdinal, c.widths.variant)}` : ''
  const rem = c.remixOrdinal != null ? `_remix_${pad(c.remixOrdinal, c.widths.variant)}` : ''
  return `${eps}${clip}${coll}${v}${rem}.${c.ext}`
}

function formatAssetFilename(c: FilenameComponents): string {
  const eps = c.episodeOrdinal != null ? `Episode_${pad(c.episodeOrdinal, c.widths.episode)}_` : ''
  const seq = `${c.typeslug}_${pad(c.sequenceOrdinal, c.widths.sequence)}`
  const v = c.variantOrdinal != null ? `_v${pad(c.variantOrdinal, c.widths.variant)}` : ''
  const rem = c.remixOrdinal != null ? `_remix_${pad(c.remixOrdinal, c.widths.variant)}` : ''
  return `${eps}${seq}${v}${rem}.${c.ext}`
}

// ─── Public entry points ────────────────────────────────────────────────────

export function buildClipDownloadFilename(inputs: ClipFilenameInputs): FilenameResult {
  const { canvasNodes, canvasEdges, nodeId, variantHistory, pickedIndex, assetPath, trigger = 'unspecified' } = inputs
  const widths = projectPaddingWidths(canvasNodes)
  const panelSet = findPanelImageGenerators(canvasNodes, canvasEdges)
  const allScriptWriters = canvasNodes.filter((n) => n.type === 'scriptWriter')
  const node = canvasNodes.find((n) => n.id === nodeId)

  let episode: { ordinal: number | null; source: EpisodeSource | null } = {
    ordinal: null,
    source: null,
  }
  let writer: CanvasNode | null = null
  if (node) {
    const walked = findBindingScriptWriterAndStoryboard(nodeId, canvasNodes, canvasEdges, panelSet)
    writer = walked.writer
    episode = extractEpisodeOrdinal(node, writer, allScriptWriters)
  }

  const noNodeFallback: {
    sequenceOrdinal: number
    collisionSuffix: string | null
    fallbackReason: FallbackReason | null
  } = { sequenceOrdinal: 1, collisionSuffix: null, fallbackReason: 'no-clip-number' }
  const seqInfo = node
    ? detectClipSequence(canvasNodes, canvasEdges, node, episode.ordinal)
    : noNodeFallback

  const variantOrds = getVariantOrdinals(variantHistory, pickedIndex)
  const ext = parseExtension(assetPath, 'video')

  let fallbackReason: FallbackReason | null = seqInfo.fallbackReason
  if (episode.ordinal == null && !writer) {
    fallbackReason = fallbackReason ?? 'no-binding-writer'
  }

  const components: FilenameComponents = {
    episodeOrdinal: episode.ordinal,
    episodeSource: episode.source,
    typeslug: 'clip',
    sequenceOrdinal: seqInfo.sequenceOrdinal,
    collisionSuffix: seqInfo.collisionSuffix,
    variantOrdinal: variantOrds.originalOrdinal,
    remixOrdinal: variantOrds.remixOrdinal,
    sourceOriginalOrdinal: variantOrds.sourceOriginalOrdinal,
    widths: { episode: widths.episode, sequence: widths.clip, variant: widths.variant },
    ext,
    fallbackReason,
  }

  const filename = formatClipFilename(components)
  filenameLog({
    event: episode.ordinal == null ? 'asset-download-filename-fallback' : 'asset-download-filename-resolved',
    trace_id: `asset-download-filename:${nodeId}:${trigger}`,
    node_id: nodeId,
    node_type: node?.type ?? 'unknown',
    asset_id: nodeId,
    media_kind: 'video',
    trigger,
    resolved_filename: filename,
    fileName: filename,
    source: trigger,
    requested: {
      episode_number: components.episodeOrdinal,
      clip_sequence: components.sequenceOrdinal,
      variant_number: components.variantOrdinal,
      collision: components.collisionSuffix ?? 'none',
    },
    episode_number: components.episodeOrdinal,
    episode_source: components.episodeSource,
    clip_sequence: components.sequenceOrdinal,
    collision: components.collisionSuffix ?? 'none',
    variant_number: components.variantOrdinal,
    fallback_reason: components.fallbackReason,
  })
  return { filename, components }
}

export function buildAssetDownloadFilename(inputs: AssetFilenameInputs): FilenameResult {
  const {
    canvasNodes,
    canvasEdges,
    nodeId,
    assetPath,
    mediaType,
    variantHistory,
    pickedIndex,
    trigger = 'node-asset',
  } = inputs
  const widths = projectPaddingWidths(canvasNodes)
  const panelSet = findPanelImageGenerators(canvasNodes, canvasEdges)
  const allScriptWriters = canvasNodes.filter((n) => n.type === 'scriptWriter')
  const node = canvasNodes.find((n) => n.id === nodeId)

  let episode: { ordinal: number | null; source: EpisodeSource | null } = {
    ordinal: null,
    source: null,
  }
  let writer: CanvasNode | null = null
  if (node) {
    const walked = findBindingScriptWriterAndStoryboard(nodeId, canvasNodes, canvasEdges, panelSet)
    writer = walked.writer
    episode = extractEpisodeOrdinal(node, writer, allScriptWriters)
  }

  const typeslug = typeslugFor(node?.type)
  const sequenceOrdinal = node ? detectAssetSequence(canvasNodes, canvasEdges, node, episode.ordinal) : 1
  const sequenceWidth = node?.type
    ? widths.sequencePerType[node.type] ?? Math.max(2, String(Math.max(sequenceOrdinal, 1)).length)
    : 2

  let variantOrdinal: number | null = null
  let remixOrdinal: number | null = null
  let sourceOriginalOrdinal: number | null = null
  if (variantHistory && variantHistory.length > 0 && pickedIndex != null) {
    const ords = getVariantOrdinals(variantHistory, pickedIndex)
    variantOrdinal = ords.originalOrdinal
    remixOrdinal = ords.remixOrdinal
    sourceOriginalOrdinal = ords.sourceOriginalOrdinal
    // For asset format, _v<VV> carries the original ordinal directly (or source-original for remixes).
    if (remixOrdinal != null && sourceOriginalOrdinal != null) variantOrdinal = sourceOriginalOrdinal
  }

  const ext = parseExtension(assetPath, mediaType)

  let fallbackReason: FallbackReason | null = null
  if (episode.ordinal == null && !writer) fallbackReason = 'no-binding-writer'

  const components: FilenameComponents = {
    episodeOrdinal: episode.ordinal,
    episodeSource: episode.source,
    typeslug,
    sequenceOrdinal,
    collisionSuffix: null,
    variantOrdinal,
    remixOrdinal,
    sourceOriginalOrdinal,
    widths: { episode: widths.episode, sequence: sequenceWidth, variant: widths.variant },
    ext,
    fallbackReason,
  }

  const filename = formatAssetFilename(components)
  filenameLog({
    event: episode.ordinal == null ? 'asset-download-filename-fallback' : 'asset-download-filename-resolved',
    trace_id: `asset-download-filename:${nodeId}:${trigger}`,
    node_id: nodeId,
    node_type: node?.type ?? 'unknown',
    asset_id: nodeId,
    media_kind: mediaType,
    trigger,
    resolved_filename: filename,
    fileName: filename,
    source: trigger,
    requested: {
      episode_number: components.episodeOrdinal,
      sequence_number: components.sequenceOrdinal,
      variant_number: components.variantOrdinal,
      collision: 'none',
    },
    episode_number: components.episodeOrdinal,
    episode_source: components.episodeSource,
    sequence_number: components.sequenceOrdinal,
    collision: 'none',
    variant_number: components.variantOrdinal,
    fallback_reason: components.fallbackReason,
  })
  return { filename, components }
}

// ─── Test-only helpers (exported for unit tests) ────────────────────────────

export const __testing = {
  parseTrailingOrdinal,
  parseEpisodeOrdinal,
  extractEpisodeOrdinal,
  projectPaddingWidths,
  findPanelImageGenerators,
  findBindingScriptWriterAndStoryboard,
  detectClipSequence,
  parseExtension,
  formatClipFilename,
  formatAssetFilename,
}
